import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { cacheLimitedStartDate, environment } from './util.service';
import { AppointmentBookingSource, AppointmentType, IAvailability, IAvailabilityOptions, IResponse } from '@signal/asp-data-commons';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, retry } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { Store } from '@ngrx/store';
import { DealerState } from '../../store/dealer/dealer.reducer';
import { selectDealerCode } from '../../store/dealer/dealer.selectors';
import { IDateAvailability, IOverview } from '@signal/asp-data-commons/lib/interfaces/availability';


@Injectable({
  providedIn: 'root'
})
export class AvailabilityService {
  private readonly aspAvailabilityApi: string;
  dateFormat = 'yyyy-MM-dd';

  constructor(private readonly http: HttpClient,
    private readonly dealerState: Store<DealerState>) {
    this.aspAvailabilityApi = environment.aspAvailabilityApi;
  }

  getAvailabilityOptions(department: 'service' | 'sales' | 'parts', appointmentType: string = AppointmentType.SERVICE): Observable<IResponse<IAvailabilityOptions>> {
    let params = new HttpParams();
    if(department === 'sales') {
      const apptTypeOBJ = {
            [AppointmentType.SERVICE]: "service",
            [AppointmentType.TEST_DRIVE]:"testdrive",
            [AppointmentType.DELIVERY]:"delivery",
            [AppointmentType.LEARNING]:"learning",
            [AppointmentType.VEHICLE_INQUIRY]: "vehicleinquiry"
      }
      params = params.append('appointmentType', apptTypeOBJ[appointmentType]);
    }
    if (department === 'parts') {
      department = 'sales';
      params = params.append('appointmentType', "learning");
    }

    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.get(`${(this.aspAvailabilityApi)}availability/${dealerCode}/${department}/options`, {params: params}).pipe(
        map((data: IResponse<IAvailabilityOptions>) => {
          return data;
        }
        )
      );
    }));
  }

  getAvailability(department: 'service' | 'sales' | 'parts',
    by: { date: string, advisorId: string, fniAdvisorId?: string, transport: string, vin? :string, appointmentId?:string, appointmentType?: string, checklistDuration?: {min: number;}, override?: {allow: boolean, date: ''} },
    opcodes: string[] = undefined, recallCodes: string[] = undefined): Observable<IResponse<IAvailability>> {
      const body = {
      transport: by.transport ? by.transport : 'DROP-OFF',
      advisorId: by.advisorId,
      vin : by.vin ? by.vin.split(',') : '',
      appointmentId : by.appointmentId ? by.appointmentId : '',
      appointmentType: by.appointmentType,
      override: {allow: false, date: ''}
    };
    if(!by.vin){
      delete body.vin;
    }


    if(!(by.appointmentId)) {
      delete body.appointmentId;
    }

    if (department == 'service') {
      body["opcodes"] = opcodes;
      if(recallCodes){
        body["recallCodes"] = recallCodes;
      }
    }
    body["appointmentBookingSource"] = AppointmentBookingSource.ASPBDC;
    if (by.appointmentType === AppointmentType.DELIVERY) {
      body["fniAdvisorId"] = by.fniAdvisorId || '-1';
    }
    
    if(by.override?.allow) {
      body["override"] = by.override;
    }
    if (department === 'parts') {
      department = 'sales';
      body["appointmentType"] = 'Learning';
    }
    
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.post(
        `${(this.aspAvailabilityApi)}availability/${dealerCode}/${department}`,
        body,
        { params: { startDate: by.date } }
      ).pipe(retry(1),
        map((data: IResponse<IAvailability>) => {
          data.data.minDate = DateTime.fromISO(data.data.minDate).minus({ month: 3 }).toFormat('MM-dd-yyyy');
          data.data.maxDate = DateTime.fromISO(data.data.maxDate).plus({ month: 3 }).toFormat('MM-dd-yyyy');
          return data;
        }
        )
      );
    }));
  }

  getScheduleDashboardSlot(department: string, startDate: string, endDate: string): Observable<IResponse<IAvailability>> {
    const body = {
      transport: 'DROP-OFF',
      advisorId: "-1",
      opcodes:[],
      appointmentBookingSource: AppointmentBookingSource.ASPBDC
    };
    const startDateTime = DateTime.fromFormat(startDate,this.dateFormat);
    if(startDateTime < cacheLimitedStartDate){
    if(startDateTime < cacheLimitedStartDate && cacheLimitedStartDate < DateTime.fromFormat(endDate,this.dateFormat) ){
        startDate = cacheLimitedStartDate.toFormat(this.dateFormat);
      }else{
        return new Observable((observer) => {
          const availability :IAvailability = {dayStartAt: '',
            minDate: startDate,
            maxDate: endDate,
            availableDates: [],
            department,
            suggestedTimes: []}
          const theDate = new Date(startDate);
          const endDate1 = new Date(endDate);
          availability.availableDates = this.getInitialAvailableDates(theDate,endDate1)
         const response: IResponse<IAvailability> = {
              status: {
                code: 200,
                messages: [],
            },
            timestamp: '',
            data: availability
         }
          
          observer.next(response);
          observer.complete();
        });
      
    }
  }
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      const departmentName = department.toLowerCase() === 'parts' ? 'sales' : department.toLowerCase();
      let params = new HttpParams().set('startDate', startDate);
      params = params.set('endDate', endDate);
      return this.http.post(`${(this.aspAvailabilityApi)}availability/${dealerCode}/${departmentName}`,
      body,
        { params }).pipe(
        map((data: IResponse<IAvailability>) => {
          if(startDate === cacheLimitedStartDate.toFormat(this.dateFormat)){
            const theDate = new Date(DateTime.fromFormat(startDate,this.dateFormat).startOf('month').startOf('week').toFormat(this.dateFormat));
          const endDate1 = new Date(cacheLimitedStartDate.plus({'days':-1}).toFormat(this.dateFormat));
          data.data.availableDates = [...this.getInitialAvailableDates(theDate,endDate1),...data.data.availableDates]
          }
          return data;
        })
      );
  }));
  }
  getNotificationDashboardData(department): Observable<IResponse<IOverview[]>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.get(`${(this.aspAvailabilityApi)}availability/${dealerCode}/service/overview?department=${department}`).pipe(
        map((data: IResponse<IOverview[]>) => {
          return data;
        }
        )
      );
    }));
  }
  private getInitialAvailableDates(theDate,endDate){
    const availabilityDates:IDateAvailability[] = []
    while (theDate <= endDate) {
      const availableDate:IDateAvailability = {
        date: DateTime.fromISO(theDate.toISOString()).toFormat(this.dateFormat),
        actualShopHrs: null,
        bookedLaborHour: 0,
        dayOfWeek: (DateTime.fromISO(theDate.toISOString())).toString(),
        garageEfficiency: null,
        isOpen: false,
        servicePersonalSetup: null,
        timeSlots: [],
        totalLaborHour: null,
        totalSlots: null}
        availabilityDates.push(availableDate)
      theDate.setDate(theDate.getDate() + 1)
    } 
    return availabilityDates;
  }

  private readonly defaultData = {data: 'default', changed: false};
  private readonly dataSubject = new BehaviorSubject<any>(this.defaultData);
  public data$: Observable<any> = this.dataSubject.asObservable();
  updateData(newData) {
    this.dataSubject.next(newData);
  }
  resetData() {
    this.dataSubject.next(this.defaultData);
  }

}
